import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/constants';
import {
  AuthContext,
  type UserProfile,
  type AuthContextType,
} from './authContext';
import { DEVICE_ID_STORAGE_KEY } from '@/lib/deviceProfile';

export type { UserProfile, AuthContextType };

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BACKEND_URL,
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  // Function to fetch comprehensive user profile data
  const fetchUserProfile = async (accessToken: string) => {
    try {
      // Set the token for this specific request
      const config = {
        headers: { Authorization: `Bearer ${accessToken}` },
      };

      // First, get the basic user info to get the user ID
      const basicResponse = await apiClient.get('/auth/me', config);

      const basicUserData = basicResponse.data;
      const userId = basicUserData.id;

      // Then fetch the comprehensive profile with summary stats
      try {
        const profileResponse = await apiClient.get(
          `/users/${userId}/profile?include=summary`,
          config,
        );

        const profileData = profileResponse.data;

        // Merge the data to have both basic info and detailed stats
        return {
          ...basicUserData,
          // Override with profile data if available
          summary: profileData.summary,
          // Keep basic user properties
          id: basicUserData.id,
          name: basicUserData.name || profileData.user_name,
          username: basicUserData.username || profileData.username,
        };
      } catch (profileError) {
        // If the detailed profile fails, fall back to basic user data
        console.warn(
          'Failed to fetch detailed profile, falling back to basic data',
          profileError,
        );
        return basicUserData;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        setIsReady(true);
        return;
      }

      try {
        // Validate token and fetch comprehensive user data
        const userData = await fetchUserProfile(savedToken);

        // Token is valid, set user data
        setToken(savedToken);
        setUser(userData);
      } catch (error) {
        // Token is invalid, clear storage
        console.error('Auth validation failed', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      } finally {
        setIsReady(true);
      }
    };

    initializeAuth();
  }, []);

  const login = async (accessToken: string, userData: UserProfile) => {
    try {
      // Fetch comprehensive profile data after login
      const fullUserData = await fetchUserProfile(accessToken);

      setToken(accessToken);
      setUser(fullUserData);
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(fullUserData));
      return fullUserData;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    const saved: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key?.startsWith('doc-digitization-draft-') ||
        key === 'read_speech_location' ||
        key === DEVICE_ID_STORAGE_KEY
      ) {
        saved.push([key, localStorage.getItem(key)!]);
      }
    }
    localStorage.clear();
    for (const [key, value] of saved) {
      localStorage.setItem(key, value);
    }
  };

  const refetchUser = async () => {
    if (token) {
      try {
        const userData = await fetchUserProfile(token);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        console.error('Error refetching user data:', error);
        // Optionally logout if token becomes invalid
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{ token, user, login, logout, isReady, refetchUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
